// /app/frontend/src/pages/seo/RelationshipTrendsAfricanDiasporaPage.jsx
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildArticleSchema,
  buildBreadcrumbSchema,
  ReportHero,
  ReportSection,
  AboutThisReport,
  PullQuote,
  BottomCTA,
} from './SEOUtils';

const URL_PATH = '/relationship-trends-african-diaspora';
const PUBLISHED_ISO = '2026-02-15';
const PUBLISHED_HUMAN = 'February 2026';

export default function RelationshipTrendsAfricanDiasporaPage() {
  const schemas = [
    organizationSchema,
    buildArticleSchema({
      headline: 'Relationship Trends Across The African Diaspora',
      description:
        'Exploring dating, marriage, family-building, community, and relationship trends across the global African diaspora.',
      urlPath: URL_PATH,
      datePublishedISO: PUBLISHED_ISO,
    }),
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Reports', url: '/reports' },
      { name: 'Relationship Trends Across The African Diaspora', url: URL_PATH },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Relationship Trends Across the African Diaspora | UnityLove"
        description="Exploring dating, marriage, family-building, community, and relationship trends across the global African diaspora."
        canonical={URL_PATH}
        schemas={schemas}
      />

      <ReportHero
        eyebrow="UnityLove Research"
        dateline={`Published ${PUBLISHED_HUMAN}`}
        headline="Relationship Trends Across The African Diaspora"
        dek="The African diaspora is one global community — and one increasingly dating across borders, generations, and cultural traditions. Here is what is shifting."
      />

      <article className="max-w-3xl mx-auto px-4 py-12">
        <ReportSection n={1} title="Global Connections">
          <p>
            The African diaspora is the largest dispersed and displaced community on earth — and in 2026, it is also one of the most connected. Video, social media, and intentional platforms have made dating across borders practical at a scale earlier generations could not have imagined. African Americans dating Nigerians. Afro-Latinos dating Afro-Caribbeans. Members of the continental diaspora dating across the Atlantic.
          </p>
          <p>
            What is emerging is a global Black dating culture — distinct from any single national or regional tradition, but rooted in the cultural fluencies they share.
          </p>
        </ReportSection>

        <ReportSection n={2} title="Shared Cultural Values">
          <p>
            Across the diaspora, certain values recur. Not universally, not without variation, but with remarkable consistency:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>The centrality of family — including extended family, not just nuclear</li>
            <li>The role of food, music, and ritual as cultural anchors</li>
            <li>Faith and spiritual practice as foundation, whether traditional, Christian, Muslim, or syncretic</li>
            <li>Intergenerational respect and the moral weight of elders</li>
            <li>A specific kind of resilience humor — grief and joy held close to each other</li>
            <li>The understanding that love is communal, not just private</li>
          </ul>
          <PullQuote>
            The diaspora is one of the few communities on earth where dating across nationalities can feel like dating someone from home.
          </PullQuote>
        </ReportSection>

        <ReportSection n={3} title="Dating Trends">
          <p>
            Three shifts are most visible in 2026:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li><strong className="text-slate-900">Cross-diaspora dating is rising.</strong> Members are increasingly open to partners from different national or regional backgrounds within the broader diaspora.</li>
            <li><strong className="text-slate-900">Intentional pacing is replacing swipe culture.</strong> Long profiles, video introductions, and community-mediated connections are taking share from rapid-swipe platforms.</li>
            <li><strong className="text-slate-900">Verification is becoming an expectation.</strong> Catfishing has eroded trust on mainstream apps; identity-verified platforms are quietly winning the serious daters.</li>
          </ul>
        </ReportSection>

        <ReportSection n={4} title="Family Building Trends">
          <p>
            Family-building is being approached with more intentionality across the diaspora. Marriage timelines, parenting philosophy, and household structure are being discussed earlier and more openly. The structures themselves are diversifying — monogamous, polyamorous, plural — but the underlying intent is converging around long-term partnership and intergenerational continuity.
          </p>
          <p>
            We are also seeing greater interest in cross-diaspora marriages, where partners bring different cultural traditions to a shared household — building family cultures that honor multiple heritages at once.
          </p>
        </ReportSection>

        <ReportSection n={5} title="Community & Identity">
          <p>
            Dating in the diaspora is not a private transaction. It happens within communities — HBCU networks, African continental community organizations, faith spaces, professional groups. The pandemic temporarily fractured many of these communities; the years since have been a slow rebuilding, increasingly augmented by digital community spaces that span the diaspora.
          </p>
          <p>
            Identity is layered for diaspora daters. National origin, racial identity, faith framing, language, generation of migration — all play roles. The platforms and communities that work best honor that layering instead of flattening it.
          </p>
        </ReportSection>

        <ReportSection n={6} title="Technology And Relationships">
          <p>
            Technology has shifted from a tool that introduces people to a tool that mediates entire relationships. Video calls bridge geographic distance. Shared media (TikTok, Spotify, WhatsApp) compresses cultural reference exchange. Translation tools enable conversation across language gaps.
          </p>
          <p>
            But technology also concentrates harms. Catfishing, fetishization, romance scams, and AI-generated profiles disproportionately target Black women in dating spaces. The technology shift is making the dating-platform-quality question existential — not just for users, but for the platforms themselves.
          </p>
        </ReportSection>

        <ReportSection n={7} title="Future Opportunities">
          <p>
            Several opportunities are visible as the diaspora dating ecosystem matures:
          </p>
          <ul className="list-disc list-inside ml-2 space-y-1.5">
            <li>Better verification standards across all platforms serving Black daters</li>
            <li>Community-led platforms designed for specific diaspora subgroups</li>
            <li>Cross-cultural relationship coaching and resources for diaspora couples</li>
            <li>Legal and financial frameworks for international relationships within the diaspora</li>
            <li>Recognition of plural family structures within faith and cultural communities</li>
          </ul>
        </ReportSection>

        <ReportSection n={8} title="Looking Ahead">
          <p>
            The next chapter of African diaspora relationships will be defined by intentionality, by community, and by the platforms that take the diaspora seriously instead of treating it as a market segment. UnityLove is one of those platforms; many more should follow. The diaspora deserves dating infrastructure that meets it where it is — culturally, globally, and at the depth that the relationships it forms deserve.
          </p>
        </ReportSection>

        <AboutThisReport published={PUBLISHED_HUMAN} />

        <section className="mt-12">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">Read Next</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/african-diaspora-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">African Diaspora Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connections across the global Black community.</p>
            </a>
            <a href="/black-love-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Love Dating App</p>
              <p className="text-sm text-slate-600 mt-1">Centering culture and connection.</p>
            </a>
            <a href="/state-of-black-relationships-2026" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">State of Black Relationships 2026</p>
              <p className="text-sm text-slate-600 mt-1">The full annual report.</p>
            </a>
            <a href="/guides" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">All UnityLove Guides</p>
              <p className="text-sm text-slate-600 mt-1">Browse the full library.</p>
            </a>
          </div>
        </section>
      </article>

      <BottomCTA />
    </div>
  );
}
